import { useEffect, useState } from "react";
import { Show, useForm } from "@refinedev/antd";
import { useList, useNavigation } from "@refinedev/core";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useMatch } from "react-router-dom";

import { AppTitle } from "../../../components";
import {
  formatDuration,
  parseDuration,
  TV_FORMAT_LABELS,
  TV_KIND_LABELS,
  TV_LEAGUES,
  TV_STATUS_LABELS,
  TvMatchRef,
  TvShow,
  TvTag,
  TvVideo,
  TvVideoForm,
  toOptions,
  tvErrorNotification,
  tvSuccessNotification,
} from "../Lib";
import {
  MatchPicker,
  TagsEditor,
  TvDelete,
  TvImageField,
  VkPreview,
} from "../Components";

export const TvVideoShow = () => {
  const route = useMatch("/tv/videos/:id");
  const id = Number(route?.params.id) || undefined;
  const { push } = useNavigation();

  const { formProps, form, queryResult, formLoading, onFinish } =
    useForm<TvVideo>({
      resource: "tv-videos",
      dataProviderName: "tvDataProvider",
      action: id ? "edit" : "create",
      id,
      redirect: false,
      ...tvSuccessNotification(id ? "Видео сохранено" : "Видео добавлено"),
      ...tvErrorNotification(),
      onMutationSuccess: ({ data }) => {
        if (!id && data?.id) push(`/tv/videos/${data.id}`);
      },
    });

  const record = queryResult?.data?.data;
  const auto = record?.source === "AUTO";
  // Выбранный матч: при смене его клубы снимаем с тегов.
  const [match, setMatch] = useState<TvMatchRef | null>(null);

  // Поля формы отличаются от ответа: ссылка ВК, дата — dayjs, длительность — «мм:сс».
  useEffect(() => {
    if (record) {
      form.setFieldsValue({
        ...record,
        vkUrl: record.embedUrl ?? undefined,
        publishedAt: record.publishedAt ? dayjs(record.publishedAt) : null,
        duration: formatDuration(record.durationSec),
      } as TvVideoForm);
      setMatch(record.match ?? null);
    }
  }, [record, form]);

  /**
   * Сменили или сняли матч — убираем из тегов клубы прежнего (новые бэкенд поставит сам),
   * сменили лигу — и тег прежней лиги. Бэкенд делает то же при сохранении.
   */
  const dropStaleTags = (prev: TvMatchRef | null, prevLeagueId?: number) => {
    const tags: TvTag[] = form.getFieldValue("tags") ?? [];
    form.setFieldValue(
      "tags",
      tags.filter(
        (t) =>
          !(
            prev &&
            t.type === "TEAM" &&
            t.leagueId === prev.leagueId &&
            (t.id === prev.homeId || t.id === prev.guestId)
          ) &&
          !(
            prevLeagueId != null &&
            t.type === "LEAGUE" &&
            t.leagueId === prevLeagueId
          )
      )
    );
  };

  const { data: showsData } = useList<TvShow>({
    resource: "tv-shows",
    dataProviderName: "tvDataProvider",
    pagination: { mode: "off" },
  });

  const kind = Form.useWatch("kind", form);
  const leagueId = Form.useWatch("leagueId", form);
  const format = Form.useWatch("format", form);

  const submit = (values: TvVideoForm) =>
    onFinish({
      kind: values.kind,
      format: values.format,
      title: values.title,
      description: values.description || null,
      coverUrl: values.coverUrl || null,
      vkUrl: values.vkUrl || null,
      durationSec: parseDuration(values.duration),
      leagueId: values.leagueId ?? null,
      gameId: values.gameId ?? null,
      matchMinute: values.matchMinute || null,
      showId: values.showId ?? null,
      episodeNo: values.episodeNo ?? null,
      status: values.status,
      publishedAt: values.publishedAt ? values.publishedAt.toISOString() : null,
      tags: (values.tags ?? []).map((t) => ({
        type: t.type,
        leagueId: t.leagueId,
        id: t.id,
      })),
    } as unknown as TvVideo);

  return (
    <Show
      isLoading={formLoading}
      title={
        <AppTitle isLoading={formLoading} canGoBack>
          {id ? `Видео №${id}` : "Новое видео"}
        </AppTitle>
      }
      breadcrumb={false}
      goBack={false}
      footerButtons={null}
      headerButtons={() =>
        id && record && !(auto && record.status === "HIDDEN") ? (
          <TvDelete
            resource="tv-videos"
            id={id}
            name={record.title}
            entityAccusative="видео"
            redirectPath="/tv/videos"
            size="middle"
            hide={auto}
          />
        ) : null
      }
    >
      {auto && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message="Видео перенесено автоматически из медиа матча, трансляции или старого раздела «Видео»"
          description="Название, описание, обложка, теги и статус правятся здесь, перенос их не трогает. Ссылку на ролик ВК ведёт перенос: меняйте её в источнике — плеер обновится сам; удалят видео там — здесь оно скроется. Удалить автовидео нельзя, только скрыть: иначе перенос заведёт его снова."
        />
      )}
      <Form<TvVideoForm>
        {...formProps}
        layout="vertical"
        onFinish={submit}
        initialValues={{
          kind: "REVIEW",
          format: "HORIZONTAL",
          status: "DRAFT",
          tags: [],
        }}
      >
        <Row gutter={32}>
          <Col xs={24} xl={14}>
            <Form.Item
              name="title"
              label="Название"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Укажите название",
                },
                { max: 255 },
              ]}
            >
              <Input placeholder="Обзор: «Урал» — «КАМАЗ» 2:0" />
            </Form.Item>
            <Form.Item
              name="description"
              label="Описание"
              rules={[{ max: 5000 }]}
            >
              <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} />
            </Form.Item>
            <Form.Item
              name="vkUrl"
              label="Ссылка на видео ВК"
              extra={
                auto
                  ? "Ссылка берётся из медиа матча, трансляции или старого раздела «Видео» — меняйте там"
                  : "Подойдёт прямая ссылка на видео или клип (vkvideo.ru/video-1_2, vk.ru/clip-1_2) или код плеера целиком. Клипы — формат «Вертикальный»"
              }
              dependencies={["status"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_, value) =>
                    getFieldValue("status") === "PUBLISHED" && !value?.trim()
                      ? Promise.reject(
                          new Error("Для публикации нужна ссылка на видео ВК")
                        )
                      : Promise.resolve(),
                }),
              ]}
            >
              <Input.TextArea
                autoSize={{ minRows: 1, maxRows: 4 }}
                placeholder="https://vkvideo.ru/video-…"
                readOnly={auto}
              />
            </Form.Item>
            <Form.Item label="Плеер">
              <VkPreview
                embedUrl={record?.embedUrl}
                vertical={format === "VERTICAL"}
              />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="kind" label="Тип" rules={[{ required: true }]}>
                  <Select options={toOptions(TV_KIND_LABELS)} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="format" label="Формат">
                  <Select options={toOptions(TV_FORMAT_LABELS)} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="status" label="Статус">
                  <Select options={toOptions(TV_STATUS_LABELS)} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="publishedAt"
                  label="Дата публикации"
                  extra="Пусто — в момент публикации; будущая дата — видео появится в это время"
                >
                  <DatePicker
                    showTime
                    format="DD.MM.YYYY HH:mm"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col xs={24} xl={10}>
            <Form.Item
              name="coverUrl"
              label="Обложка"
              extra={
                // у матчевых видео сайт рисует гербы, счёт и подпись поверх обложки
                kind === "REVIEW" ||
                kind === "PRESS_CONFERENCE" ||
                kind === "BROADCAST_RECORD"
                  ? "Только фон 16:9, без текста и гербов — гербы, счёт и подпись сайт положит поверх. Пусто — фон карточки матча или паттерн лиги"
                  : "16:9 для горизонтальных, 9:16 для вертикальных"
              }
            >
              <TvImageField />
            </Form.Item>
            <Form.Item name="leagueId" label="Лига">
              <Select
                allowClear
                options={TV_LEAGUES}
                placeholder="Лига видео"
                onChange={(next?: number) => {
                  dropStaleTags(
                    match,
                    leagueId != null && next !== leagueId ? leagueId : undefined
                  );
                  setMatch(null);
                  form.setFieldValue("gameId", undefined);
                }}
              />
            </Form.Item>
            <Form.Item
              name="gameId"
              label="Матч"
              extra="Клубы матча и лига добавятся в теги сами"
            >
              <MatchPicker
                leagueId={leagueId}
                initial={record?.match}
                onChange={(_, next) => {
                  if (match && next?.matchId !== match.matchId)
                    dropStaleTags(match);
                  setMatch(next ?? null);
                }}
              />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="matchMinute"
                  label="Минута момента"
                  extra="Моменты — хайлайты (тип «Хайлайт»), привязанные к матчу; на сайте идут вертикальными карточками, поэтому лучше вертикальный формат. Минута — для подписи и сортировки"
                >
                  <Input maxLength={16} placeholder="38′" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="duration"
                  label="Длительность"
                  rules={[
                    {
                      validator: (_, value) =>
                        Number.isNaN(parseDuration(value))
                          ? Promise.reject(
                              new Error("Формат мм:сс или секунды")
                            )
                          : Promise.resolve(),
                    },
                  ]}
                >
                  <Input placeholder="12:34" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={16}>
                <Form.Item
                  name="showId"
                  label="Шоу или спецпроект"
                  rules={[
                    {
                      required: kind === "SHOW_EPISODE",
                      message: "Для выпуска шоу выберите шоу",
                    },
                  ]}
                >
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Не относится к шоу"
                    options={(showsData?.data ?? []).map((s) => ({
                      value: s.id,
                      label: s.title,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="episodeNo" label="Выпуск №">
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="tags" label="Теги">
              <TagsEditor defaultLeagueId={leagueId} />
            </Form.Item>
          </Col>
        </Row>

        <Space align="center">
          <Button type="primary" htmlType="submit" loading={formLoading}>
            {id ? "Сохранить" : "Добавить видео"}
          </Button>
          {record && (
            <Typography.Text type="secondary">
              Просмотров: {record.viewsCount} · лайков: {record.likesCount} ·
              изменено {dayjs(record.updatedAt).format("DD.MM.YYYY HH:mm")}
            </Typography.Text>
          )}
        </Space>
      </Form>
    </Show>
  );
};
